import React, { useEffect, useRef, useState } from 'react';
import './mapStyles.css';

const SearchPlaces = ({ changeLocation }) => {
    const [inputValue, setInputValue] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [isServiceReady, setIsServiceReady] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    // Check if the new Places API (v1) is available
    useEffect(() => {
        const checkGoogleMaps = () => {
            if (window.google && window.google.maps) {
                setIsServiceReady(true);
                setError(null);
            } else {
                setTimeout(checkGoogleMaps, 1000);
            }
        };
        checkGoogleMaps();
    }, []);

    useEffect(() => {
        if (!inputValue || inputValue.length < 2) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }

        if (!isServiceReady) {
            setError('Search service is not ready yet');
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            try {
                const response = await fetch(
                    'https://places.googleapis.com/v1/places:searchText',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
                            'X-Goog-FieldMask':
                                'places.displayName,places.formattedAddress,places.location',
                        },
                        body: JSON.stringify({
                            textQuery: inputValue,
                            pageSize: 5,
                            locationBias: {
                                circle: {
                                    center: { latitude: 37.7749, longitude: -122.4194 },
                                    radius: 50000,
                                },
                            },
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.places && data.places.length > 0) {
                    // Map the API result to predictions
                    const mapped = data.places.map((place) => ({
                        description: place.formattedAddress,
                        place_id: place.name ? place.name.split('/').pop() : place.formattedAddress,
                        structured_formatting: {
                            main_text: place.displayName?.text || '',
                            secondary_text: place.formattedAddress,
                        },
                        geometry: {
                            location: {
                                lat: place.location.latitude,
                                lng: place.location.longitude,
                            },
                        },
                    }));
                    setPredictions(mapped);
                    setShowPredictions(true);
                    setError(null);

                    // Automatically set location to the first place in the array
                    if (mapped.length > 0 && mapped[0].geometry && mapped[0].geometry.location) {
                        changeLocation(mapped[0].geometry.location);
                    }
                } else {
                    setPredictions([]);
                    setShowPredictions(false);
                }
            } catch (err) {
                console.warn('Text Search (New) error:', err);
                setPredictions([]);
                setShowPredictions(false);
                setError('Search error: ' + err.message);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, isServiceReady]);

    const handlePredictionClick = (prediction) => {
        if (prediction.geometry && prediction.geometry.location) {
            changeLocation(prediction.geometry.location);
            setInputValue(prediction.description);
            setShowPredictions(false);
            setError(null);
        } else {
            setError('Location details error: no location data');
        }
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setError(null);
    };

    const handleInputBlur = () => {
        setTimeout(() => setShowPredictions(false), 200);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowPredictions(false);
        }
    };

    return (
        <div className="search-container">
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={() =>
                    predictions.length > 0 && setShowPredictions(true)
                }
                onKeyDown={handleKeyDown}
                placeholder={
                    isServiceReady
                        ? 'Search for places...'
                        : 'Loading search service...'
                }
                className="search-input"
                disabled={!isServiceReady}
                autoComplete="off"
            />
            {error && (
                <div
                    style={{
                        color: '#d32f2f',
                        fontSize: '12px',
                        marginTop: '4px',
                        padding: '4px',
                        backgroundColor: '#ffeaea',
                        border: '1px solid #ffcdd2',
                        borderRadius: '4px',
                    }}
                >
                    {error}
                </div>
            )}
            {showPredictions && predictions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {predictions.map((prediction) => (
                        <div
                            key={prediction.place_id}
                            onClick={() => handlePredictionClick(prediction)}
                            className="autocomplete-item"
                            role="option"
                            aria-selected={false}
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handlePredictionClick(prediction);
                                }
                            }}
                        >
                            <div className="prediction-main">
                                {prediction.structured_formatting?.main_text ||
                                    prediction.description}
                            </div>
                            {prediction.structured_formatting
                                ?.secondary_text && (
                                <div className="prediction-secondary">
                                    {
                                        prediction.structured_formatting
                                            .secondary_text
                                    }
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {showPredictions &&
                predictions.length === 0 &&
                inputValue.length >= 2 &&
                !error && (
                    <div className="autocomplete-dropdown">
                        <div
                            className="autocomplete-item no-results"
                            style={{
                                color: '#666',
                                fontStyle: 'italic',
                                cursor: 'default',
                            }}
                        >
                            No places found for "{inputValue}"
                        </div>
                    </div>
                )}
        </div>
    );
};

export default SearchPlaces;
